import api from '@/services/api';
import authApi from '@/services/authApi';

import { useAuthStore } from '@/store/auth';

let isRefreshing = false;

let pendingRequests = [];


function processQueue(error, accessToken = null) {

    pendingRequests.forEach(({ resolve, reject }) => {

        if (error) {
            reject(error);
        } else {
            resolve(accessToken);
        }

    });

    pendingRequests = [];
}


export function setupAuthInterceptor() {

    // --------------------------------------------------
    // REQUEST INTERCEPTOR
    // --------------------------------------------------

    api.interceptors.request.use(
        (config) => {

            const authStore = useAuthStore();

            if (authStore.accessToken) {

                config.headers.Authorization =
                    `Bearer ${authStore.accessToken}`;

            }

            return config;
        },

        (error) => Promise.reject(error)
    );


    // --------------------------------------------------
    // RESPONSE INTERCEPTOR
    // --------------------------------------------------

    api.interceptors.response.use(

        (response) => response,

        async (error) => {

            const originalRequest = error.config;

            // Si no es un 401, dejamos que Axios
            // gestione el error normalmente.
            if (error.response?.status !== 401) {
                return Promise.reject(error);
            }

            // Evita bucles infinitos.
            if (originalRequest._retry) {
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            const authStore = useAuthStore();

            const refreshToken = authStore.refreshToken;

            // No tenemos refresh token.
            if (!refreshToken) {

                authStore.logout();

                return Promise.reject(error);
            }


            // --------------------------------------------------
            // YA HAY UN REFRESH EN CURSO
            // --------------------------------------------------

            if (isRefreshing) {

                return new Promise((resolve, reject) => {

                    pendingRequests.push({
                        resolve,
                        reject,
                    });

                }).then((accessToken) => {

                    originalRequest.headers.Authorization =
                        `Bearer ${accessToken}`;

                    return api(originalRequest);

                }).catch((error) => {

                    throw error;

                });
            }


            // --------------------------------------------------
            // HACEMOS REFRESH
            // --------------------------------------------------

            isRefreshing = true;

            try {

                const response = await authApi.post(
                    '/auth/refresh',
                    null,
                    {
                        headers: {
                            Authorization: `Bearer ${refreshToken}`,
                        },
                    }
                );

                const {
                    access_token,
                    refresh_token,
                } = response.data;


                if (!access_token) {
                    throw new Error(
                        'El servidor no devolvió un nuevo access_token'
                    );
                }


                // Actualizamos Pinia + localStorage.
                authStore.setTokens({
                    access_token,
                    refresh_token,
                });


                // Liberamos las peticiones que estaban
                // esperando el refresh.
                processQueue(
                    null,
                    access_token
                );


                // Reintentamos la petición original.
                originalRequest.headers.Authorization =
                    `Bearer ${access_token}`;

                return api(originalRequest);

            } catch (refreshError) {

                // Las peticiones pendientes también fallan.
                processQueue(
                    refreshError,
                    null
                );

                // El refresh token ya no es válido.
                authStore.logout();

                return Promise.reject(refreshError);

            } finally {

                isRefreshing = false;

            }
        }
    );
}

export default setupAuthInterceptor;